import { Print, ViewInAr, Wifi, Receipt } from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { FC, useState } from "react";
import { Colors } from "../../../configs/colors";
import PosSettingsLayout from "../../../components/layouts/pos-layout/pos-settings-layout";
import useTrayPrinter from "../../../hooks/useTrayPrinter";
import { ITrayPrintRequest } from "../../../typings/interfaces/print/ticket-print-request";

const TRAY_BASE_URL = "http://localhost:8535";

const TEST_PRINT_DATA: ITrayPrintRequest = {
  copyType: "receipt",
  invoice: "TEST-001",
  department: "DRY CLEANING",
  dropOff: "05/18/2026 · 10:00 AM",
  pickupLabel: "Ready By",
  pickup: "05/20/2026",
  pickupAfter: "05:00 PM",
  customer: "Test Customer",
  customerId: "0",
  cashier: "Cashier",
  cashierId: "0",
  storeName: "Presto Valet",
  storeSubtitle: "Test Print",
  storeAddress: "123 Test Street",
  storePhone: "555-1234",
  storeWebsite: "www.prestovalet.net",
  items: [{ qty: 1, description: "Test Item", price: 10.0 }],
  subtotal: 10.0,
  discount: 0,
  total: 10.0,
  paidBy: "CASH",
  paid: 10.0,
  balance: 0,
  specialStamp: "*** TEST PRINT ***",
};

type ActionStatus = "idle" | "loading" | "ok" | "error";

const StatusChip: FC<{ status: ActionStatus; okText: string; errText: string }> = ({
  status,
  okText,
  errText,
}) => {
  if (status === "idle") return null;
  if (status === "loading")
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: "0.5rem", mt: "0.5rem" }}>
        <CircularProgress size="0.85rem" sx={{ color: Colors.SECONDARY }} />
        <Typography sx={{ fontSize: "0.9rem", color: Colors.SECONDARY }}>
          Please wait…
        </Typography>
      </Box>
    );
  const isOk = status === "ok";
  return (
    <Typography
      sx={{
        mt: "0.5rem",
        fontSize: "0.9rem",
        fontWeight: "bold",
        color: isOk ? Colors.SUCCESS_GREEN : Colors.ERROR_RED,
      }}
    >
      {isOk ? `✓ ${okText}` : `✗ ${errText}`}
    </Typography>
  );
};

const PrinterSetupContent: FC = () => {
  const { printTicket, isPrinting } = useTrayPrinter();
  const [connStatus, setConnStatus] = useState<ActionStatus>("idle");
  const [printStatus, setPrintStatus] = useState<ActionStatus>("idle");

  const handleTestConnection = async () => {
    setConnStatus("loading");
    try {
      const res = await fetch(`${TRAY_BASE_URL}/device-id`, {
        signal: AbortSignal.timeout(5000),
      });
      setConnStatus(res.ok ? "ok" : "error");
    } catch {
      setConnStatus("error");
    }
  };

  const handleTestBillPrint = async () => {
    setPrintStatus("loading");
    const ok = await printTicket(TEST_PRINT_DATA);
    setPrintStatus(ok ? "ok" : "error");
  };

  return (
    <>
      <Typography
        variant="h5"
        sx={{
          color: Colors.TEXT_GRAY_DARK,
          fontSize: "3.428571428571429rem !important",
          fontWeight: "bold",
          marginBottom: "2rem",
        }}
      >
        Printer Setup
      </Typography>

      {/* Device & Letter Spacing */}
      <Box
        sx={{
          bgcolor: Colors.BACKGROUND_GRAY,
          borderRadius: "0.71rem",
          width: "47.85714285714286rem",
          height: "10.71428571428571rem",
          padding: "1.5rem",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          marginBottom: "2rem",
          marginTop: "3rem",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            marginBottom: 1,
            paddingLeft: "2rem",
          }}
        >
          <Typography
            sx={{
              color: Colors.TEXT_GRAY_DARK,
              marginRight: "2rem",
              fontWeight: "bold",
            }}
          >
            Device
          </Typography>
          <Select
            defaultValue="device1"
            sx={{
              width: "27.5rem",
              height: "2.164285714285714rem",
              marginLeft: "7.8rem",
              bgcolor: Colors.WHITE,
              "& .MuiSelect-icon": {
                color: Colors.WHITE,
                bgcolor: Colors.SECONDARY,
                border: `0.1428571428571429rem solid ${Colors.SECONDARY}`,
                borderRadius: "0.41rem",
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: Colors.WHITE,
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: Colors.SECONDARY,
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: Colors.SECONDARY,
              },
              "& .MuiSelect-select": {
                textAlign: "center",
              },
            }}
          >
            <MenuItem value="device1">Device 1</MenuItem>
            <MenuItem value="device2">Device 2</MenuItem>
            <MenuItem value="device3">Device 3</MenuItem>
            <MenuItem value="device4">Device 4</MenuItem>
            <MenuItem value="device5">Device 5</MenuItem>
          </Select>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            paddingLeft: "2rem",
          }}
        >
          <Typography
            sx={{
              color: Colors.TEXT_GRAY_DARK,
              marginRight: "2rem",
              fontWeight: "bold",
            }}
          >
            Letter Spacing
          </Typography>
          <TextField
            type="number"
            sx={{
              width: "27.5rem",
              height: "2.164285714285714rem",
              marginLeft: "4rem",
              bgcolor: Colors.WHITE,
              borderRadius: "0.71rem",
              "& .MuiOutlinedInput-root": {
                "& fieldset": { borderColor: Colors.WHITE },
                "&:hover fieldset": { borderColor: Colors.SECONDARY },
                "&.Mui-focused fieldset": { borderColor: Colors.SECONDARY },
                height: "100%",
              },
              "& input[type=number]": {
                paddingRight: "1.2rem",
                "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
                  WebkitAppearance: "auto",
                  width: "1.7rem",
                  height: "1.7rem",
                  cursor: "pointer",
                  position: "relative",
                  right: "1px",
                  opacity: 1,
                },
              },
            }}
            inputProps={{ style: { textAlign: "center" } }}
          />
        </Box>
      </Box>

      {/* Test Actions */}
      <Box
        sx={{
          bgcolor: Colors.BACKGROUND_GRAY,
          borderRadius: "0.71rem",
          width: "47.85714285714286rem",
          padding: "1.5rem 2rem",
          display: "flex",
          flexDirection: "column",
          gap: "1.5rem",
          marginBottom: "4rem",
        }}
      >
        <Typography
          sx={{ color: Colors.TEXT_GRAY_DARK, fontWeight: "bold", fontSize: "1.1rem" }}
        >
          Printer Tests
        </Typography>

        {/* Test Connection */}
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: "1.5rem" }}>
            <Button
              variant="outlined"
              startIcon={<Wifi />}
              disabled={connStatus === "loading"}
              onClick={handleTestConnection}
              sx={{
                color: Colors.SECONDARY,
                borderColor: Colors.SECONDARY,
                textTransform: "none",
                fontSize: "1rem",
                minWidth: "14rem",
                height: "3rem",
                "&:hover": { borderColor: Colors.SECONDARY, bgcolor: "#E3F2FD" },
              }}
            >
              Test Printer Connection
            </Button>
            <Typography sx={{ color: Colors.TEXT_GRAY_DARK, fontSize: "0.85rem" }}>
              Checks if the tray app is reachable on this device.
            </Typography>
          </Box>
          <StatusChip
            status={connStatus}
            okText="Printer tray app is connected."
            errText="Cannot reach printer tray app. Make sure it is running."
          />
        </Box>

        {/* Test Bill Print */}
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: "1.5rem" }}>
            <Button
              variant="outlined"
              startIcon={<Receipt />}
              disabled={isPrinting || printStatus === "loading"}
              onClick={handleTestBillPrint}
              sx={{
                color: Colors.PRIMARY,
                borderColor: Colors.PRIMARY,
                textTransform: "none",
                fontSize: "1rem",
                minWidth: "14rem",
                height: "3rem",
                "&:hover": { borderColor: Colors.PRIMARY, bgcolor: Colors.NOTIFIC_GREEN },
              }}
            >
              Test Bill Print
            </Button>
            <Typography sx={{ color: Colors.TEXT_GRAY_DARK, fontSize: "0.85rem" }}>
              Sends a sample receipt to the printer.
            </Typography>
          </Box>
          <StatusChip
            status={printStatus}
            okText="Test bill printed successfully."
            errText="Test bill print failed. Check printer connection."
          />
        </Box>
      </Box>

      {/* Cancel / Save */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", width: "47.85714285714286rem" }}>
        <Button
          variant="outlined"
          sx={{
            color: Colors.TEXT_GRAY_DARK,
            borderColor: Colors.TEXT_GRAY_DARK,
            textTransform: "none",
            marginRight: "2rem",
            fontSize: "1.714285714285714rem",
            width: "12.64142857142857rem",
            height: "4.142857142857143rem",
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          sx={{
            bgcolor: Colors.PRIMARY,
            color: Colors.WHITE,
            textTransform: "none",
            fontSize: "1.714285714285714rem",
            width: "12.64142857142857rem",
            height: "4.142857142857143rem",
          }}
        >
          Save
        </Button>
      </Box>
    </>
  );
};

const tabs = [
  {
    id: "printer_setup",
    text: "Printer Setup",
    icon: <Print />,
    content: <PrinterSetupContent />,
  },
  {
    id: "racks",
    text: "Racks",
    icon: <ViewInAr />,
    content: (
      <Typography
        variant="h5"
        sx={{
          color: Colors.TEXT_GRAY_DARK,
          fontSize: "3.428571428571429rem !important",
          fontWeight: "bold",
          marginBottom: "2rem",
        }}
      >
        Racks
      </Typography>
    ),
  },
  {
    id: "developer_tools",
    text: "Developer Tools",
    icon: <ViewInAr />,
    content: (
      <Typography
        variant="h5"
        sx={{
          color: Colors.TEXT_GRAY_DARK,
          fontSize: "3.428571428571429rem !important",
          fontWeight: "bold",
          marginBottom: "2rem",
        }}
      >
        Developer Tools
      </Typography>
    ),
  },
];

export const SettingsPage: FC = () => {
  const [activeTab, setActiveTab] = useState(tabs[0].id);
  return (
    <PosSettingsLayout
      tabs={tabs}
      activeTab={activeTab}
      setActiveTab={setActiveTab}
    >
      {tabs.find((tab) => tab.id === activeTab)?.content}
    </PosSettingsLayout>
  );
};
